import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { useSubscription, formatINR } from "@/lib/subscription";
import { useAuth } from "@/lib/auth";
import {
  Sparkles, Building2, Stethoscope, Armchair, CreditCard, Check,
  ArrowRight, ArrowLeft, Plus, X, Loader2, Upload, Image as ImageIcon,
} from "lucide-react";

const STEPS = [
  { id: 1, title: "Welcome",          icon: Sparkles },
  { id: 2, title: "Clinic details",   icon: Building2 },
  { id: 3, title: "Your doctors",     icon: Stethoscope },
  { id: 4, title: "Chairs & staff",   icon: Armchair },
  { id: 5, title: "Choose plan",      icon: CreditCard },
  { id: 6, title: "Optional features", icon: Check },
];

const FEATURE_TOGGLES = [
  { id: "whatsapp",       label: "WhatsApp automation", desc: "Auto reminders, consent links, warranty cards" },
  { id: "online_booking", label: "Online booking",       desc: "Public link — patients book themselves via viyora.co.in/book/…" },
  { id: "google_reviews", label: "Google Reviews",       desc: "Auto-request reviews after treatment" },
  { id: "inventory",      label: "Inventory management", desc: "Track materials, stock alerts" },
  { id: "ai_copilot",     label: "Viyora Copilot",       desc: "AI-powered clinical assistant (Clinic plan only)" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { sub, refresh } = useSubscription();
  const { refresh: refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState({
    clinic_name: sub?.clinic_name || "",
    address: "",
    phone: "",
    email: "",
    gstin: "",
    logo_url: "",
    doctors: [{ name: "", speciality: "", visiting: false }],
    chairs_count: 1,
    reception_staff_count: 1,
    plan_intent: "clinic_monthly",
    feature_prefs: ["whatsapp", "online_booking"],
  });

  const updateField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canProceed = () => {
    if (step === 2) return data.clinic_name?.trim().length > 1;
    if (step === 3) return data.doctors.some((d) => d.name.trim());
    if (step === 4) return data.chairs_count >= 1;
    return true;
  };

  const validateStep = (s) => {
    if (s === 2) {
      const missing = [];
      if (!data.clinic_name || data.clinic_name.trim().length < 2) missing.push("Clinic name");
      if (!data.phone || data.phone.trim().length < 6) missing.push("Phone");
      if (missing.length) {
        toast.error(`Please add: ${missing.join(", ")}`);
        return false;
      }
    }
    if (s === 3) {
      const doctors = (data.doctors || []).filter((d) => d.name && d.name.trim());
      if (doctors.length === 0) {
        toast.error("Add at least one doctor (or your own name if solo)");
        return false;
      }
    }
    if (s === 4) {
      if (!data.chairs_count || data.chairs_count < 1) {
        toast.error("Please add at least 1 chair");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(6, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        doctors: data.doctors.filter((d) => d.name.trim()),
      };
      await api.post("/onboarding/complete", payload);
      toast.success("You're all set!");
      await refresh();
      await refreshUser?.();
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-linen text-ink flex flex-col" data-testid="onboarding-wizard">
      {/* Top bar with progress — compact single row */}
      <header className="border-b border-[#E8EAE9] bg-white sticky top-0 z-10">
        <div className="container-lg py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <img src="/viyora-icon-64.png" alt="Viyora" className="w-7 h-7 " />
            <span className="font-display text-base tracking-tight">Viyora</span>
          </div>
          <div className="text-[11px] text-ink-soft hidden md:block">Step {step} of {STEPS.length}</div>
          {/* Step chips folded into the same row on lg+ */}
          <div className="hidden lg:flex items-center gap-1.5 ml-auto">
            {STEPS.map((s, i) => {
              const active = s.id === step;
              const done = s.id < step;
              return (
                <div key={s.id} className="flex items-center gap-1.5">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] transition ${active ? "bg-pine text-linen" : done ? "bg-[#E8F0EB] text-[#2D6A4F]" : "text-ink-soft"}`}>
                    <s.icon className="w-3 h-3" /> {s.title}
                  </div>
                  {i < STEPS.length - 1 && <div className="w-3 h-px bg-[#E8EAE9]" />}
                </div>
              );
            })}
          </div>
        </div>
        <div className="h-1 bg-linen relative overflow-hidden">
          <div className="h-full bg-pine transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 container-lg py-4 md:py-6 max-w-3xl">
        {step === 1 && <StepWelcome onNext={next} clinicName={sub?.clinic_name} trialDays={30} />}
        {step === 2 && <StepClinic data={data} update={updateField} />}
        {step === 3 && <StepDoctors data={data} update={updateField} />}
        {step === 4 && <StepResources data={data} update={updateField} />}
        {step === 5 && <StepPlan data={data} update={updateField} />}
        {step === 6 && <StepFeatures data={data} update={updateField} planIntent={data.plan_intent} />}
      </main>

      {/* Footer nav */}
      <footer className="border-t border-[#E8EAE9] bg-white">
        <div className="container-lg py-4 flex items-center justify-between max-w-3xl mx-auto">
          <button
            onClick={back}
            disabled={step === 1}
            data-testid="btn-onboarding-back"
            className={`inline-flex items-center gap-2 text-sm ${step === 1 ? "text-ink-disabled cursor-not-allowed" : "text-ink hover:opacity-80"}`}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 6 ? (
            <button
              onClick={next}
              data-testid="btn-onboarding-next"
              className="inline-flex items-center gap-2 rounded-full bg-pine text-linen px-6 py-2.5 text-sm hover:opacity-90"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={saving}
              data-testid="btn-onboarding-finish"
              className="inline-flex items-center gap-2 rounded-full bg-pine text-linen px-6 py-2.5 text-sm hover:opacity-90 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Setting up…" : "Finish setup"}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepWelcome({ onNext, clinicName, trialDays }) {
  return (
    <div className="text-center py-4" data-testid="step-welcome">
      <span className="overline text-ink-soft">Let&apos;s get you set up</span>
      <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tight">Welcome to Viyora.</h1>
      <p className="mt-3 text-sm text-ink-mid max-w-xl mx-auto">
        {clinicName ? `Hi ${clinicName}! ` : "Hi there! "}
        In the next 3 minutes we&apos;ll set up your clinic — chairs, doctors, and the features you care about. All features are unlocked for the next <strong>{trialDays} days</strong>, no card required.
      </p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
        {[
          { icon: Building2, label: "Clinic details", copy: "Name, address, GST — pre-fills every invoice, consent, warranty." },
          { icon: Stethoscope, label: "Your team", copy: "Add doctors + their specialities. Invite reception staff later." },
          { icon: Armchair, label: "Chairs & plan", copy: "Set up chairs, pick a plan — Solo or Clinic. Change anytime." },
        ].map((h) => (
          <div key={h.label} className="rounded-2xl bg-white border border-[#E8EAE9] p-4">
            <h.icon className="w-5 h-5 text-ink" />
            <div className="mt-2 font-display text-base">{h.label}</div>
            <div className="mt-1 text-xs text-ink-mid">{h.copy}</div>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        data-testid="btn-onboarding-start"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-pine text-linen px-6 py-2.5 text-sm hover:opacity-90 transition"
      >
        Let&apos;s begin <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepClinic({ data, update }) {
  return (
    <div data-testid="step-clinic">
      <span className="overline text-ink-soft">Step 2</span>
      <h2 className="mt-1 font-display text-2xl md:text-3xl tracking-tight">Tell us about your clinic.</h2>
      <p className="mt-1.5 text-sm text-ink-mid">This info pre-fills every invoice, consent, and warranty card. You can edit anytime in Settings.</p>

      <div className="mt-5">
        <LogoUpload value={data.logo_url} onChange={(url) => update("logo_url", url)} />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Clinic name *" required>
          <input value={data.clinic_name} onChange={(e) => update("clinic_name", e.target.value)}
                 data-testid="ob-clinic-name" placeholder="e.g. Sunrise Dental Care"
                 className="input" />
        </Field>
        <Field label="Phone">
          <input value={data.phone} onChange={(e) => update("phone", e.target.value)}
                 data-testid="ob-clinic-phone" placeholder="+91 98xxxxxxxx" className="input" />
        </Field>
        <Field label="Email">
          <input value={data.email} onChange={(e) => update("email", e.target.value)}
                 type="email" data-testid="ob-clinic-email" placeholder="contact@your-clinic.in" className="input" />
        </Field>
        <Field label="GSTIN (optional)">
          <input value={data.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())}
                 data-testid="ob-clinic-gstin" placeholder="27AAAAA0000A1Z5" className="input font-mono" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Address">
            <textarea value={data.address} onChange={(e) => update("address", e.target.value)}
                      data-testid="ob-clinic-address" rows={3} placeholder="Street, city, state, pincode"
                      className="input resize-none" />
          </Field>
        </div>
      </div>
      <StyleShim />
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepDoctors({ data, update }) {
  const addDoctor = () => update("doctors", [...data.doctors, { name: "", speciality: "", visiting: false }]);
  const removeDoctor = (idx) => update("doctors", data.doctors.filter((_, i) => i !== idx));
  const setDoctor = (idx, k, v) => update("doctors", data.doctors.map((d, i) => i === idx ? { ...d, [k]: v } : d));

  return (
    <div data-testid="step-doctors">
      <span className="overline text-ink-soft">Step 3</span>
      <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">Who are your doctors?</h2>
      <p className="mt-2 text-sm text-ink-mid">Add each doctor&apos;s name and speciality. You can invite them to the app (with email + role) later from Settings.</p>

      <div className="mt-8 space-y-3">
        {data.doctors.map((doc, idx) => (
          <div key={idx} className="rounded-2xl bg-white border border-[#E8EAE9] p-4" data-testid={`ob-doctor-${idx}`}>
            <div className="grid grid-cols-12 gap-3">
              <input
                value={doc.name} onChange={(e) => setDoctor(idx, "name", e.target.value)}
                data-testid={`ob-doctor-name-${idx}`}
                placeholder="Doctor name (e.g. Dr. Priya Rao)"
                className="input col-span-12 md:col-span-5"
              />
              <input
                value={doc.speciality} onChange={(e) => setDoctor(idx, "speciality", e.target.value)}
                data-testid={`ob-doctor-speciality-${idx}`}
                placeholder="Speciality (e.g. Endodontist)"
                className="input col-span-12 md:col-span-4"
              />
              <label className="col-span-8 md:col-span-2 flex items-center gap-2 text-xs text-ink-mid">
                <input
                  type="checkbox" checked={doc.visiting}
                  onChange={(e) => setDoctor(idx, "visiting", e.target.checked)}
                  data-testid={`ob-doctor-visiting-${idx}`}
                />
                Visiting
              </label>
              <button
                type="button" onClick={() => removeDoctor(idx)}
                disabled={data.doctors.length === 1}
                data-testid={`ob-remove-doctor-${idx}`}
                className="col-span-4 md:col-span-1 text-ink-soft hover:text-[#B94A48] disabled:opacity-20 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button" onClick={addDoctor} data-testid="ob-add-doctor"
        className="mt-4 inline-flex items-center gap-2 text-sm text-ink hover:opacity-80"
      >
        <Plus className="w-4 h-4" /> Add another doctor
      </button>
      <StyleShim />
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepResources({ data, update }) {
  const setChairs = (n) => update("chairs_count", Math.max(1, Math.min(8, n)));
  const setStaff = (n) => update("reception_staff_count", Math.max(0, Math.min(20, n)));

  return (
    <div data-testid="step-resources">
      <span className="overline text-ink-soft">Step 4</span>
      <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">Chairs &amp; staff.</h2>
      <p className="mt-2 text-sm text-ink-mid">We&apos;ll pre-create the chairs so your calendar is ready on day one.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-widget">
          <div className="overline text-ink-soft">Chairs / Operatories</div>
          <div className="mt-4 flex items-center gap-4">
            <button onClick={() => setChairs(data.chairs_count - 1)} data-testid="ob-chairs-minus" className="w-10 h-10 rounded-full border border-[#E8EAE9] hover:bg-linen font-mono">−</button>
            <div className="font-display text-5xl min-w-[60px] text-center" data-testid="ob-chairs-count">{data.chairs_count}</div>
            <button onClick={() => setChairs(data.chairs_count + 1)} data-testid="ob-chairs-plus" className="w-10 h-10 rounded-full border border-[#E8EAE9] hover:bg-linen font-mono">+</button>
          </div>
          <div className="mt-2 text-xs text-ink-mid">Solo plan: 1 chair · Clinic plan: up to 8 chairs</div>
        </div>

        <div className="card-widget">
          <div className="overline text-ink-soft">Reception staff</div>
          <div className="mt-4 flex items-center gap-4">
            <button onClick={() => setStaff(data.reception_staff_count - 1)} data-testid="ob-staff-minus" className="w-10 h-10 rounded-full border border-[#E8EAE9] hover:bg-linen font-mono">−</button>
            <div className="font-display text-5xl min-w-[60px] text-center" data-testid="ob-staff-count">{data.reception_staff_count}</div>
            <button onClick={() => setStaff(data.reception_staff_count + 1)} data-testid="ob-staff-plus" className="w-10 h-10 rounded-full border border-[#E8EAE9] hover:bg-linen font-mono">+</button>
          </div>
          <div className="mt-2 text-xs text-ink-mid">Invite them from Settings anytime.</div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepPlan({ data, update }) {
  const plans = [
    { id: "solo_monthly",   name: "Solo",   price: 79900,   interval: "monthly", for: "Single doctor · 1 chair" },
    { id: "clinic_monthly", name: "Clinic", price: 149900,  interval: "monthly", for: "Up to 8 chairs · multi-doctor" },
    { id: "solo_yearly",    name: "Solo",   price: 799900,  interval: "yearly",  for: "Single doctor · 1 chair · Save 16%" },
    { id: "clinic_yearly",  name: "Clinic", price: 1499900, interval: "yearly",  for: "Up to 8 chairs · multi-doctor · Save 16%" },
  ];
  const [cycle, setCycle] = useState(data.plan_intent?.endsWith("yearly") ? "yearly" : "monthly");
  const visible = plans.filter((p) => p.interval === cycle);

  return (
    <div data-testid="step-plan">
      <span className="overline text-ink-soft">Step 5</span>
      <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">Choose the plan you like.</h2>
      <p className="mt-2 text-sm text-ink-mid">You won&apos;t be charged today — everything stays unlocked for 30 days. Pick what fits your practice; you can switch later.</p>

      <div className="mt-6 inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white">
        {["monthly", "yearly"].map((c) => (
          <button key={c} onClick={() => setCycle(c)} data-testid={`ob-cycle-${c}`}
                  className={`px-4 py-1.5 text-xs rounded-full ${cycle === c ? "bg-pine text-linen" : "text-ink-mid"}`}>
            {c === "monthly" ? "Monthly" : "Yearly · Save 16%"}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((p) => {
          const selected = data.plan_intent === p.id;
          const highlight = p.name === "Clinic";
          return (
            <button
              key={p.id}
              onClick={() => update("plan_intent", p.id)}
              data-testid={`ob-plan-${p.id}`}
              className={`text-left rounded-2xl p-6 border-2 transition ${
                selected ? "border-pine bg-pine text-linen" : `bg-white ${highlight ? "border-pine/40" : "border-[#E8EAE9]"} hover:border-pine`
              }`}
            >
              <div className="flex items-baseline justify-between">
                <div className={`font-display text-2xl ${selected ? "text-linen" : ""}`}>{p.name}</div>
                {highlight && !selected && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDF3ED] text-[#8A3F1E]">Popular</span>}
                {selected && <Check className="w-5 h-5 text-linen" />}
              </div>
              <div className={`mt-3 font-display text-4xl ${selected ? "text-linen" : ""}`}>
                {formatINR(p.price)}<span className={`text-sm font-sans ${selected ? "text-linen/60" : "text-ink-soft"}`}> / {p.interval === "yearly" ? "year" : "mo"}</span>
              </div>
              <div className={`mt-1 text-xs ${selected ? "text-linen/70" : "text-ink-mid"}`}>{p.for}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepFeatures({ data, update, planIntent }) {
  const isSolo = planIntent?.startsWith("solo");
  const toggle = (id) => {
    const set = new Set(data.feature_prefs);
    if (set.has(id)) set.delete(id); else set.add(id);
    update("feature_prefs", Array.from(set));
  };

  return (
    <div data-testid="step-features">
      <span className="overline text-ink-soft">Step 6 · Optional</span>
      <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">Which features should we set up?</h2>
      <p className="mt-2 text-sm text-ink-mid">We&apos;ll turn these on and show them in the dashboard. You can toggle any of them from Settings later.</p>

      <div className="mt-8 space-y-3">
        {FEATURE_TOGGLES.map((f) => {
          const selected = data.feature_prefs.includes(f.id);
          const locked = f.id === "ai_copilot" && isSolo;
          return (
            <label
              key={f.id}
              data-testid={`ob-feature-${f.id}`}
              className={`flex items-start gap-4 p-4 rounded-2xl border cursor-pointer transition ${
                locked ? "bg-linen border-[#E8EAE9] opacity-70" :
                selected ? "border-pine bg-white shadow-sm" : "border-[#E8EAE9] bg-white hover:border-pine/40"
              }`}
            >
              <input
                type="checkbox"
                checked={selected && !locked}
                disabled={locked}
                onChange={() => !locked && toggle(f.id)}
                className="mt-1 w-4 h-4 accent-pine"
              />
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  {f.label}
                  {locked && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDF3ED] text-[#8A3F1E]">Clinic plan</span>}
                </div>
                <div className="mt-0.5 text-xs text-ink-mid">{f.desc}</div>
                {locked && (
                  <div className="mt-1 text-xs text-[#8A3F1E]">
                    Viyora Copilot is available on the Clinic plan. <button type="button" onClick={() => update("plan_intent", "clinic_monthly")} className="underline">Switch to Clinic</button>
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function LogoUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const resolvedSrc = value ? (value.startsWith("http") ? value : `${backendUrl}${value}`) : "";

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Please upload a PNG, JPG or WEBP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clinic/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.logo_url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiError(err, "Could not upload logo"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete("/clinic/logo");
      onChange("");
    } catch {
      onChange("");
    }
  };

  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-5 flex items-center gap-5" data-testid="ob-logo-block">
      <div className="w-20 h-20 rounded-2xl bg-linen border border-[#E8EAE9] grid place-items-center overflow-hidden shrink-0">
        {resolvedSrc ? (
          <img src={resolvedSrc} alt="Clinic logo preview" className="w-full h-full object-contain" data-testid="ob-logo-preview" />
        ) : (
          <ImageIcon className="w-7 h-7 text-ink-disabled" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink">Clinic logo</div>
        <div className="mt-0.5 text-xs text-ink-mid">
          Shown on your patient booking page, dashboard and invoices. PNG or JPG, up to 2 MB. Square works best.
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="ob-logo-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : value ? "Replace logo" : "Upload logo"}
          </button>
          {value && (
            <button
              type="button"
              onClick={remove}
              data-testid="ob-logo-remove-btn"
              className="text-xs text-ink-mid hover:text-[#B94A48]"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="ob-logo-input"
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink/80 mb-1.5">
        {label}{required && <span className="text-[#B94A48]"></span>}
      </div>
      {children}
    </label>
  );
}

function StyleShim() {
  return (
    <style>{`
      .input {
        width: 100%;
        border-radius: 12px;
        border: 1px solid #E8EAE9;
        background: #fff;
        padding: 10px 14px;
        font-size: 14px;
        outline: none;
        transition: box-shadow .15s, border-color .15s;
      }
      .input:focus { border-color: #1A362B; box-shadow: 0 0 0 3px rgba(26,54,43,0.12); }
    `}</style>
  );
}
